import React, { useState } from 'react';
import './DashboardCommon.css';
import smLogo from '../../assets/SM_logo-removebg.png';
import ParticipatingTenderList from '../ParticipatingTenderList';
import MyFinancialBidTasks from '../MyFinancialBidTasks';
import MyAssignedTasks from '../MyAssignedTasks';
import EmdList from '../EmdList';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../NotificationBell';

function SMFinanceDashboard({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [profileDropdown, setProfileDropdown] = useState(false);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home' },
    { id: 'financial-bids', label: 'My Price Bid Tasks', icon: 'dollar' },
    { id: 'myTasks', label: 'My Tasks', icon: 'checkSquare' },
    { id: 'emds', label: 'EMDs', icon: 'briefcase' },
    { id: 'participating', label: 'Participating Tenders', icon: 'check' },
  ];

  const getPageInfo = () => {
    if (activeMenu === 'dashboard') return { breadcrumb: [], title: 'Dashboard' };
    const item = menuItems.find(m => m.id === activeMenu);
    return item ? { breadcrumb: [item.label], title: item.label } : { breadcrumb: [], title: '' };
  };

  const pageInfo = getPageInfo();

  const stats = [
    { label: 'Price Bids', value: '–', change: 'My Tasks', color: '#10b981' },
    { label: 'Participating', value: '–', change: 'View', color: '#3b82f6' },
  ];

  const renderIcon = (name) => {
    const icons = {
      home: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>,
      check: <><polyline points="20 6 9 17 4 12"></polyline></>,
      dollar: <><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></>,
      briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></>,
      logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></>,
      menu: <><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></>,
      bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></>,
      checkSquare: <><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></>,
    };
    return icons[name] || null;
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'financial-bids':
        return <MyFinancialBidTasks user={user} />;
      case 'myTasks':
        return <MyAssignedTasks user={user} />;
      case 'emds':
        return <EmdList />;
      case 'participating':
        return <ParticipatingTenderList user={user} canManage={false} />;
      case 'dashboard':
      default:
        return (
          <>
            <div className="content-header">
              <h2>Welcome, {user?.full_name || user?.username || 'Finance'}!</h2>
              <p>View and complete price bid assignments for participating tenders.</p>
            </div>
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-content">
                    <span className="stat-label">{stat.label}</span>
                    <span className="stat-value">{stat.value}</span>
                    <span className="stat-change" style={{ color: stat.color }}>{stat.change}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="placeholder-section">
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('financial-bids')}>
                <h3>My Price Bid Tasks</h3>
                <p>View and complete price bid assignments.</p>
                <button className="placeholder-btn active">My Tasks</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('participating')}>
                <h3>Participating Tenders</h3>
                <p>View tenders and open price bid details.</p>
                <button className="placeholder-btn active">View Tenders</button>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} data-tooltip={sidebarOpen ? 'Collapse' : 'Expand'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {sidebarOpen ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
          </svg>
        </button>
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => setActiveMenu(item.id)}
              data-tooltip={item.label}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon(item.icon)}</svg>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
      <header className="topbar">
        <div className="topbar-left">
          <img src={smLogo} alt="SM Systems" className="topbar-logo" />
          <div className="topbar-divider"></div>
          <h1 className="page-title">SM Finance</h1>
        </div>
        <div className="topbar-right">
          <NotificationBell />
          <ThemeToggle />
          <div className="user-profile-wrapper">
            <button className="user-profile" onClick={() => setProfileDropdown(!profileDropdown)}>
              <div className="avatar">{(user?.full_name || user?.username)?.charAt(0) || 'F'}</div>
              <div className="user-info">
                <span className="user-name">{user?.full_name || user?.username || 'Finance'}</span>
                <span className="user-role">SM Finance</span>
              </div>
              <svg className={`dropdown-arrow ${profileDropdown ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {profileDropdown && (
              <div className="profile-dropdown">
                <button className="dropdown-item" onClick={onLogout}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon('logout')}</svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        <div className="content">
          <div className="content-title-area">
            {pageInfo.breadcrumb.length > 0 && (
              <nav className="breadcrumb" aria-label="Breadcrumb">
                {pageInfo.breadcrumb.map((label, i) => (
                  <span key={i}>
                    {i > 0 && <span className="breadcrumb-sep"> / </span>}
                    <span>{label}</span>
                  </span>
                ))}
              </nav>
            )}
            <h2 className="content-page-title">{pageInfo.title}</h2>
          </div>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default SMFinanceDashboard;
